import { getAppConfig } from '@clarity-website/config/useAppConfig';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import React from 'react';
import { Cookies } from 'react-cookie';

const MIDWAY_URL = 'https://midway-auth.amazon.com/SSO';
const RETRY_ATTEMPTS = 3;
const COOKIE_EXPIRY_BUFFER_SECONDS = 60; // Cookie expires 1 minute before token
const CACHE_INVALIDATION_BUFFER_SECONDS = 240; // Cache invalidation 4 minutes before token expires (3 minutes before cookie expires)
const DEFAULT_TOKEN_EXPIRY_SECONDS = 7200; // Default to 2 hours
const ID_TOKEN = 'id_token';
const STATE = 'state';

// Query keys
const MIDWAY_JWT_TOKEN_KEY = 'midwayJwtToken';

interface JwtMidwayPayload extends JwtPayload {
  nonce: string;
}

interface MidwayTokenData {
  token: string;
  user: string;
  decodedToken: JwtMidwayPayload;
}

/**
 * React Query hook to get Midway JWT token with automatic caching and refresh
 */
export function useMidwayJwtToken() {
  const queryClient = useQueryClient();
  const timeoutRef = React.useRef<NodeJS.Timeout>();

  // Clean up timeout on unmount
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return useQuery<MidwayTokenData>({
    queryKey: [MIDWAY_JWT_TOKEN_KEY],
    queryFn: async () => {
      // Create fresh params for this specific request
      const params = createMidwayParams();
      const tokenData = await fetchMidwayToken(params);

      // Set cookie after successful token fetch
      setMidwayTokenCookie(tokenData.token, tokenData.decodedToken);

      // Schedule cache invalidation and refetch before token expires
      // This happens 4 minutes before token expires (3 minutes before cookie expires)
      const tokenExpiry = getTokenExpiry(tokenData.decodedToken);
      const timeUntilCacheInvalidation =
        (tokenExpiry -
          Math.round(Date.now() / 1000) -
          CACHE_INVALIDATION_BUFFER_SECONDS) *
        1000;

      if (timeUntilCacheInvalidation > 0) {
        // Store timeout ID for cleanup
        timeoutRef.current = setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: [MIDWAY_JWT_TOKEN_KEY] });
        }, timeUntilCacheInvalidation);
      }

      return tokenData;
    },
    // Set staleTime to token expiration minus buffer - will be calculated dynamically
    staleTime: 0, // We handle staleness through scheduled invalidation
    retry: RETRY_ATTEMPTS,
    retryDelay: () => 500, // Fixed 500ms delay as in original implementation
    onError: (error) => {
      console.error('Error fetching Midway JWT token:', error);
    },
  });
}

/**
 * Gets the token expiry timestamp, defaulting to 2 hours from now if not available
 */
function getTokenExpiry(decodedToken: JwtMidwayPayload): number {
  return (
    decodedToken.exp ??
    Math.round(Date.now() / 1000) + DEFAULT_TOKEN_EXPIRY_SECONDS
  );
}

/**
 * Sets the Midway token as a cookie with proper expiration
 */
function setMidwayTokenCookie(
  midwayJwt: string,
  decodedToken: JwtMidwayPayload,
): void {
  try {
    const config = getAppConfig();
    const cookieStorage = config.Auth.cookieStorage;
    const cookies = new Cookies();

    // Set expiration date to 1 minute before token expiration
    const tokenExpiry = getTokenExpiry(decodedToken);
    const cookieExpirationTimestamp =
      (tokenExpiry - COOKIE_EXPIRY_BUFFER_SECONDS) * 1000;
    const expirationDate = new Date(cookieExpirationTimestamp);

    cookies.set('amzn_sso_token', midwayJwt, {
      domain: cookieStorage.domain,
      secure: true,
      sameSite: 'strict',
      expires: expirationDate,
      path: '/',
    });
  } catch (error) {
    console.error('Failed to set Midway token cookie:', error);
  }
}

/**
 * Create fresh params for each request to avoid race conditions
 */
function createMidwayParams() {
  const nonce = randomString(24);
  return {
    scope: 'openid',
    response_type: ID_TOKEN,
    client_id: encodeURIComponent(`${window.location.host}`),
    redirect_uri: encodeURIComponent(`${window.location}`),
    nonce,
  };
}

/**
 * Core token fetching logic - now accepts params as argument
 */
export async function fetchMidwayToken(
  params: ReturnType<typeof createMidwayParams>,
): Promise<MidwayTokenData> {
  const queryParams = Object.entries(params)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');
  const url = `${MIDWAY_URL}?${queryParams}`;

  const token = await fetch(url, { credentials: 'include' }).then(
    (response) => {
      if (response.status === 401) {
        // Redirect the user to Midway login
        window.location.replace(`${MIDWAY_URL}/redirect?${queryParams}`);
        throw new Error('Authentication required - redirecting to login');
      }
      removeMidwaySearchParams();
      return response.text();
    },
  );

  const decodedToken = jwtDecode<JwtMidwayPayload>(token);
  const user = decodedToken.sub!; // The midway user

  // Verify nonce
  if (decodedToken.nonce !== params.nonce) {
    console.error('Error when verifying token nonce');
    throw new Error('Token nonce verification failed');
  }

  return {
    token,
    user,
    decodedToken,
  };
}

/**
 * Generates a cryptographically secure random string for nonce
 */
function randomString(length = 12): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join(
    '',
  );
}

/**
 * Removes from current location the URL params returned by Midway (e.g. 'id_token',
 * 'state') after login redirection and updates browser history so they do not
 * appear in the current URL
 */
function removeMidwaySearchParams(): void {
  const newLocation = removeLocationSearchParameters(ID_TOKEN, STATE);
  window.history.replaceState({}, '', newLocation);
}

/**
 * Removes the passed keys from the current location search (query)
 * @param keys
 * @returns a new location without the keys
 */
function removeLocationSearchParameters(...keys: string[]): string {
  const location = window.location;
  const queryParams = new URLSearchParams(location.search);
  keys.forEach((key) => queryParams.delete(key));
  const queryString = queryParams.toString();
  const base = location.href.replace(location.search, '');
  const newUrl = `${base}${queryString === '' ? '' : '?'}${queryString}`;
  return newUrl;
}
